import React, { useState, useEffect, useRef } from 'react';
import {
    OverlayTrigger,
    Tooltip,
    Form,
    Pagination
} from 'react-bootstrap';
import {
    BiError,
    BiVideo
} from 'react-icons/bi';
import {
    BsCheckCircleFill,
    BsEnvelopeExclamation,
    BsEnvelopeOpen,
    BsEnvelopeX,    
    BsPersonSlash,
    BsPersonX,
    BsXCircleFill,
    BsDashCircle,
    BsFileEarmarkText,
    BsPencilSquare
} from 'react-icons/bs';
import { FaUserAlt, FaUserClock, FaUserSlash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../api';
import { useParams } from 'react-router-dom';
import { useBreadcrumbs } from '../../../Routes/BreadcrumbContext';
import { createBreadcrumbs } from '../../../utils/breadcrumbUtils';
import { TipoBancaNome } from '../../../model/banca/Banca';
import { EnviarEmailHeteroidentificacao } from '../../../viewmodel/ComunicacaoCandidato/EmailHeteroidentificacao';
import ConfirmarEnvioTermoModal from './ConfirmarEnvioTermoModal';
import AlterarResultadoModal from './AlterarResultadoModal';
import PermissaoWrapper from '../../../components/PermissaoWrapper';
import './CandidatosBancaPage.css';

interface CandidatoEdital {
    id: string;
    inscricao: string;
    candidatoId: string;
    editalId: string;
    nomeEdital: string;
}

interface CandidatoBancaResponse {
    candidatoId: string;
    nome: string;
    nomeSocial: string;
    email: string | null;
    candidatoEditais: CandidatoEdital[];
    statusCandidato: StatusCandidato;
    resultado: ResultadoCandidato;
    file_name: string | null;
    fileName?: string | null;
    emailResultadoConfirmado: boolean | null;
    emailResultadoEnviado: boolean | null;
}

enum StatusCandidato {
    NAO_AVALIADO = "NAO_AVALIADO",
    INCONCLUSIVO = "INCONCLUSIVO",
    VALIDADO = "VALIDADO",
    NAO_VALIDADO = "NAO_VALIDADO",
    DELETADO = "DELETADO",
    AUSENTE = "AUSENTE"
}

enum ResultadoCandidato {
    VALIDADO = "VALIDADO",
    NAO_VALIDADO = "NAO_VALIDADO",
    AUSENTE = "AUSENTE",
    INCONCLUSIVO = "INCONCLUSIVO"
}

const StatusDisplayNames: Record<StatusCandidato, string> = {
    [StatusCandidato.NAO_AVALIADO]: "Não avaliado",
    [StatusCandidato.INCONCLUSIVO]: "Inconclusivo",
    [StatusCandidato.VALIDADO]: "Validado",
    [StatusCandidato.NAO_VALIDADO]: "Não validado",
    [StatusCandidato.DELETADO]: "Deletado",
    [StatusCandidato.AUSENTE]: "Ausente"
};

const ResultadoDisplayNames: Record<ResultadoCandidato, string> = {
    [ResultadoCandidato.VALIDADO]: "Validado",
    [ResultadoCandidato.NAO_VALIDADO]: "Não validado",
    [ResultadoCandidato.AUSENTE]: "Ausente",
    [ResultadoCandidato.INCONCLUSIVO]: "Inconclusivo"
};

const CandidatosBancaPage = () => {
    const { processoId, bancaId } = useParams<{ processoId: string, bancaId: string }>();
    const { setBreadcrumbs } = useBreadcrumbs();
    const [candidatos, setCandidatos] = useState<CandidatoBancaResponse[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [votacao, setVotacao] = useState(false);
    const [tipoBanca, setTipoBanca] = useState<TipoBancaNome | null>(null);
    const [filtros, setFiltros] = useState({
        nome: '',
        inscricao: '',
        status: '',
        resultado: '',
        video: '',
        email: ''
    });
    const [filtrosDebounced, setFiltrosDebounced] = useState(filtros);
    const debounceRef = useRef<NodeJS.Timeout>();
    const [statusBanca, setStatusBanca] = useState<string | null>(null);
    const [enviandoTodos, setEnviandoTodos] = useState(false);
    const [showModalEnvio, setShowModalEnvio] = useState(false);
    const [dadosEnvio, setDadosEnvio] = useState<{ candidatoId?: string; nomeCompleto?: string; email?: string | null; quantidade?: number }>({});
    const [showModalResultado, setShowModalResultado] = useState(false);
    const [candidatoSelecionado, setCandidatoSelecionado] = useState<{ id: string; nome: string; resultado: ResultadoCandidato | null }>({ id: '', nome: '', resultado: null });

    useEffect(() => {
        setBreadcrumbs(createBreadcrumbs.fromProcessos([
            { path: `/processos/${processoId}/bancas`, breadcrumb: 'Bancas' },
            { path: `/processos/${processoId}/bancas/${bancaId}/candidatos`, breadcrumb: 'Candidatos' }
        ]));
    }, [setBreadcrumbs, processoId, bancaId]);

    const fetchBancaInfo = async () => {
        try {
            const response = await api.get(`processos/${processoId}/bancas/${bancaId}`);
            setTipoBanca(response.data.tipoBanca.nome);
            setStatusBanca(response.data.status);
        } catch (err: any) {
            console.error('Erro ao carregar informações da banca:', err);
        }
    };

    useEffect(() => {
        if (bancaId) {
            fetchBancaInfo();
        }
    }, [bancaId]);

    const fetchCandidatos = async () => {
        try {
            setLoading(true);
            const params: any = { page, size, votacao };
            
            // Adiciona filtros apenas se tiverem valor
            Object.entries(filtrosDebounced).forEach(([key, value]) => {
                if (value.trim()) {
                    params[key] = value.trim();
                }
            });
            
            const response = await api.get(`/bancas/${bancaId}/candidatos`, { params });
            setCandidatos(response.data.content);
            setTotalPages(response.data.totalPages);
            setTotalElements(response.data.totalElements ?? 0);
            setError(null);
            //toast.success('Candidatos carregados com sucesso');
        } catch (err: any) {
            const errorMsg = err.response?.data?.message || 'Erro ao carregar candidatos';
            setError(errorMsg);
            setCandidatos([]);
            toast.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (bancaId) {
            fetchCandidatos();
        }
    }, [bancaId, page, size, votacao, filtrosDebounced]);

    useEffect(() => {
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        
        debounceRef.current = setTimeout(() => {
            setFiltrosDebounced(filtros);
        }, 800);
        
        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
        };
    }, [filtros]);

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
    };

    const handleSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSize(Number(e.target.value));
        setPage(0);
    };

    const handleVotacaoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setVotacao(e.target.checked);
        setPage(0);
    };

    const handleFiltroChange = (campo: string, valor: string) => {
        setFiltros(prev => ({ ...prev, [campo]: valor }));
        setPage(0);
    };

    const limparFiltros = () => {
        const filtrosLimpos = { nome: '', inscricao: '', status: '', resultado: '', video: '', email: '' };
        setFiltros(filtrosLimpos);
        setFiltrosDebounced(filtrosLimpos);
        setPage(0);
    };

    const handleEnviarTermo = (candidatoId: string, nomeCompleto: string, candidatoEmail: string | null) => {
        if (!candidatoEmail) {
            toast.error('Candidato não possui e-mail cadastrado');
            return;
        }
        
        if (statusBanca !== 'FINALIZADA') {
            toast.error('Só é possível enviar termos quando a banca estiver finalizada');
            return;
        }
        
        setDadosEnvio({ candidatoId, nomeCompleto, email: candidatoEmail });
        setShowModalEnvio(true);
    };

    const confirmarEnvioTermo = async () => {
        setShowModalEnvio(false);
        
        try {
            await EnviarEmailHeteroidentificacao.enviarTermoIndividual(bancaId!, dadosEnvio.candidatoId!);
            toast.success('Termo enviado com sucesso!');
            fetchCandidatos();
        } catch (error: any) {
            const errorMsg = error.response?.data?.message || 'Erro ao enviar termo';
            toast.error(errorMsg);
        }
    };

    const handleEnviarTermoTodos = async () => {
        if (statusBanca !== 'FINALIZADA') {
            toast.error('Só é possível enviar termos quando a banca estiver finalizada');
            return;
        }

        try {
            const resultado = await EnviarEmailHeteroidentificacao.enviarEmailResultadoCandidato({ 
                bancaId: bancaId!, 
                apenasContagem: true 
            });
            
            if (resultado.totalElegivel === 0) {
                toast.warning('Nenhum candidato elegível para receber o termo');
                return;
            }

            setDadosEnvio({ quantidade: resultado.totalElegivel });
            setShowModalEnvio(true);
        } catch (error: any) {
            const errorMsg = error.response?.data?.message || 'Erro ao verificar candidatos';
            toast.error(errorMsg);
        }
    };

    const confirmarEnvioTermoTodos = async () => {
        setShowModalEnvio(false);
        setEnviandoTodos(true);
        
        try {
            const resultado = await EnviarEmailHeteroidentificacao.enviarEmailResultadoCandidato({ bancaId: bancaId! });
            const totalEnviados = resultado.emailsEnviadosValidado + resultado.emailsEnviadosNaoValidado;
            
            if (totalEnviados > 0) {
                toast.success(
                    `Termos enviados com sucesso! ` +
                    `Validados: ${resultado.emailsEnviadosValidado} | ` +
                    `Não Validados: ${resultado.emailsEnviadosNaoValidado}`,
                    { autoClose: 5000 }
                );
            } else {
                toast.info('Nenhum termo foi enviado. Verifique se há candidatos com resultado e e-mail cadastrado.');
            }
            
            fetchCandidatos();
        } catch (error: any) {
            const errorMsg = error.response?.data?.message || 'Erro ao enviar termos';
            toast.error(errorMsg);
        } finally {
            setEnviandoTodos(false);
        }
    };

    const getStatusClass = (status?: StatusCandidato, resultado?: ResultadoCandidato): string => {
        const statusToCheck = resultado ? resultado : status;

        switch (statusToCheck) {
            case 'VALIDADO':
                return 'validado';
            case 'NAO_VALIDADO':
                return 'naovalidado';
            case StatusCandidato.NAO_AVALIADO:
                return 'indefinido';
            case 'INCONCLUSIVO':
                return 'inconclusivo';
            case 'AUSENTE':
                return 'ausente';
            case StatusCandidato.DELETADO:
                return 'deletado';
            default:
                return '';
        }
    };

    const getResultadoIcon = (resultado?: ResultadoCandidato): JSX.Element => {
        switch (resultado) {
            case ResultadoCandidato.VALIDADO:
                return <BsCheckCircleFill color="darkgreen" size={16} />;
            case ResultadoCandidato.NAO_VALIDADO:
                return <BsXCircleFill color="darkred" size={16} />;
            case ResultadoCandidato.AUSENTE:
                return <FaUserSlash size={16} />;
            case ResultadoCandidato.INCONCLUSIVO:
                return <FaUserClock size={16} />;
            default:
                return <BsDashCircle color="#999" size={16} />;
        }
    };

    const getStatusIcon = (status?: StatusCandidato): JSX.Element => {
        switch (status) {
            case StatusCandidato.VALIDADO:
                return <BsCheckCircleFill color="darkgreen" size={16} />;
            case StatusCandidato.NAO_VALIDADO:
                return <BsXCircleFill color="darkred" size={16} />;
            case StatusCandidato.NAO_AVALIADO:
                return <FaUserAlt size={16} />;
            case StatusCandidato.INCONCLUSIVO:
                return <FaUserClock size={16} />;
            case StatusCandidato.AUSENTE:
                return <FaUserSlash size={16} />;
            case StatusCandidato.DELETADO:
                return <BsPersonSlash size={16} />;
            default:
                return <BiError size={16} />;
        }
    };

    const getResultadoTooltip = (resultado?: ResultadoCandidato): JSX.Element => {
        const displayName = resultado ? ResultadoDisplayNames[resultado] : 'Não avaliado na Banca';
        return (
            <Tooltip id="tooltip-resultado">
                <span>{displayName}</span>
            </Tooltip>
        );
    };

    const getStatusTooltip = (status?: StatusCandidato): JSX.Element => {
        const displayName = status ? StatusDisplayNames[status] : 'Desconhecido';
        return (
            <Tooltip id="tooltip-status">
                <span>{displayName}</span>
            </Tooltip>
        );
    };

    const getEmailStatusIcon = (candidato: CandidatoBancaResponse): { icon: JSX.Element; tooltipMsg: string } => {
        if (candidato.resultado === ResultadoCandidato.AUSENTE || candidato.resultado === ResultadoCandidato.INCONCLUSIVO) {
            const resultadoNome = ResultadoDisplayNames[candidato.resultado];
            return {
                icon: <BsDashCircle size={16} color="#999" />,
                tooltipMsg: `Termo não pode ser enviado para resultado ${resultadoNome}`,
            };
        }

        if (!candidato.emailResultadoEnviado && !candidato.emailResultadoConfirmado) {
            return {
                icon: <BsEnvelopeX size={16} color="maroon" />,
                tooltipMsg: 'Termo não enviado',
            };
        }

        if (candidato.emailResultadoEnviado && !candidato.emailResultadoConfirmado) {
            return {
                icon: <BsEnvelopeExclamation size={16} color="goldenrod" />,
                tooltipMsg: 'Termo enviado, aguardando confirmação',
            };
        }

        if (candidato.emailResultadoEnviado && candidato.emailResultadoConfirmado) {
            return {
                icon: <BsEnvelopeOpen size={16} color="olivedrab" />,
                tooltipMsg: 'Termo enviado e confirmado',
            };
        }

        return {
            icon: <></>,
            tooltipMsg: '',
        };
    };

    const renderVideoIndicator = (candidato: CandidatoBancaResponse) => {
        if (candidato.statusCandidato === StatusCandidato.AUSENTE ||
            candidato.resultado === ResultadoCandidato.AUSENTE) {
            return (
                <OverlayTrigger placement="top" overlay={<Tooltip>Não se aplica - Candidato Ausente</Tooltip>}>
                    <div className="d-inline-flex align-items-center justify-content-center">
                        <FaUserSlash size={16} />
                    </div>
                </OverlayTrigger>
            );
        }

    if (candidato.file_name || candidato.fileName) {
            return (
                <OverlayTrigger placement="top" overlay={<Tooltip>Vídeo enviado</Tooltip>}>
                    <div className="d-inline-flex align-items-center justify-content-center">
                        <BiVideo size={16} color="green" />
                    </div>
                </OverlayTrigger>
            );
        }

        return (
            <OverlayTrigger placement="top" overlay={<Tooltip>Vídeo não enviado</Tooltip>}>
                <div className="d-inline-flex align-items-center justify-content-center">
                    <BsPersonX size={16} color="red" />
                </div>
            </OverlayTrigger>
        );
    };

    const handleAlterarResultado = (candidatoId: string, nome: string, resultado: ResultadoCandidato | null) => {
        setCandidatoSelecionado({ id: candidatoId, nome, resultado });
        setShowModalResultado(true);
    };

    const confirmarAlteracaoResultado = async (novoResultado: string) => {
        setShowModalResultado(false);
        
        try {
            await api.patch(`/bancas/${bancaId}/candidatos/${candidatoSelecionado.id}/resultado`, {
                resultado: novoResultado
            });
            toast.success('Resultado atualizado com sucesso!');
            fetchCandidatos();
        } catch (error: any) {
            const errorMsg = error.response?.data?.message || 'Erro ao atualizar resultado';
            toast.error(errorMsg);
        }
    };

    if (loading) {
        return (
            <div className="candidatos-container">
                <div className="table-container">
                    <div className="loading-container">
                        <div>Carregando candidatos...</div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="candidatos-container">
                <div className="alert alert-danger">{error}</div>
            </div>
        );
    }
    const renderInscricoes = (candidatoEditais: CandidatoEdital[]) => {
        if (!candidatoEditais || candidatoEditais.length === 0) {
            return '-';
        } else if (candidatoEditais.length === 1) {
            return (
                <div>
                    <strong>{candidatoEditais[0].inscricao}</strong> / {candidatoEditais[0].nomeEdital}
                </div>
            );
        } else {
            return (
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip id={`tooltip-${candidatoEditais[0].candidatoId}`}>
                            {candidatoEditais.map((edital, idx) => (
                                <div key={edital.id}>
                                    <strong>{candidatoEditais[0].inscricao}</strong> / {edital.nomeEdital}
                                    {idx < candidatoEditais.length - 1 && <br />}
                                </div>
                            ))}
                        </Tooltip>
                    }
                >
                    <span className="badge bg-secondary cursor-pointer">{candidatoEditais.length} inscrições</span>
                </OverlayTrigger>
            );
        }
    };

    return (
        <div className="candidatos-container">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="mb-0">Candidatos da Banca</h2>
                <div className="d-flex justify-content-end">
                    <button
                        className="btn-enviar-termo"
                        onClick={handleEnviarTermoTodos}
                        disabled={statusBanca !== 'FINALIZADA' || enviandoTodos || candidatos.length === 0}
                        title={statusBanca !== 'FINALIZADA' ? 'Banca precisa estar finalizada' : 'Enviar termo para todos os candidatos'}
                        style={{ padding: '8px 16px', fontSize: '14px' }}
                    >
                        <BsFileEarmarkText size={16} style={{ marginRight: '6px' }} />
                        {enviandoTodos ? 'Enviando...' : 'Enviar Termo para Todos'}
                    </button>
                </div>
            </div>
            <div className="table-container">
                <div className="table-responsive">
                    <table className="candidatos-table">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Inscrições/Editais</th>
                                <th>Status (Processo)</th>
                                <th>Resultado na Banca</th>
                                {tipoBanca === TipoBancaNome.VIRTUAL && <th>Vídeo</th>}
                                <th style={{ backgroundColor: '#e8f5e9' }}>Status Termo</th>
                                <th style={{ backgroundColor: '#e8f5e9' }}>Enviar Termo</th>
                                <PermissaoWrapper permissoesRequiridas={['administrador']}>
                                    <th style={{ backgroundColor: '#fff3cd' }}>Alterar Resultado</th>
                                </PermissaoWrapper>
                            </tr>
                        </thead>
                        <tbody>
                            {candidatos.length === 0 ? (
                                <tr>
                                    <td colSpan={(tipoBanca === TipoBancaNome.VIRTUAL ? 1 : 0) + 7} className="empty-state">
                                        Nenhum candidato encontrado.
                                    </td>
                                </tr>
                            ) : (
                                candidatos.map((candidato) => {
                                    const emailStatus = getEmailStatusIcon(candidato);
                                    return (
                                        <tr key={candidato.candidatoId} className={getStatusClass(candidato.statusCandidato, candidato.resultado)}>
                                            <td className="nome-cell">
                                                <div className="nome-principal">{candidato.nome}</div>
                                                {candidato.nomeSocial && (
                                                    <div className="nome-social">
                                                        {candidato.nomeSocial}
                                                    </div>
                                                )}
                                            </td>
                                            <td>{renderInscricoes(candidato.candidatoEditais)}</td>
                                            <td>
                                                <OverlayTrigger placement="top" overlay={getStatusTooltip(candidato.statusCandidato)}>
                                                    <div className="d-inline-flex align-items-center justify-content-center">
                                                        {getStatusIcon(candidato.statusCandidato)}
                                                    </div>
                                                </OverlayTrigger>
                                            </td>
                                            <td>
                                                <OverlayTrigger placement="top" overlay={getResultadoTooltip(candidato.resultado)}>
                                                    <div className="d-inline-flex align-items-center justify-content-center">
                                                        {getResultadoIcon(candidato.resultado)}
                                                    </div>
                                                </OverlayTrigger>
                                            </td>
                                            {tipoBanca === TipoBancaNome.VIRTUAL && <td>{renderVideoIndicator(candidato)}</td>}
                                            <td style={{ backgroundColor: '#f1f8f4', textAlign: 'center' }}>
                                                {emailStatus.tooltipMsg && (
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>{emailStatus.tooltipMsg}</Tooltip>}>
                                                        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            {emailStatus.icon}
                                                        </div>
                                                    </OverlayTrigger>
                                                )}
                                            </td>
                                            <td style={{ backgroundColor: '#f1f8f4', textAlign: 'center' }}>
                                                <OverlayTrigger 
                                                    placement="top" 
                                                    overlay={
                                                        <Tooltip>
                                                            {!candidato.email 
                                                                ? 'Candidato sem e-mail cadastrado' 
                                                                : statusBanca !== 'FINALIZADA'
                                                                ? 'Banca precisa estar finalizada'
                                                                : 'Enviar termo'}
                                                        </Tooltip>
                                                    }
                                                >
                                                    <span style={{ display: 'inline-block' }}>
                                                        <button
                                                            className="btn-enviar-termo"
                                                            onClick={() => handleEnviarTermo(candidato.candidatoId, candidato.nomeSocial || candidato.nome, candidato.email)}
                                                            disabled={!candidato.email || statusBanca !== 'FINALIZADA'}
                                                        >
                                                            <BsFileEarmarkText size={14} />
                                                        </button>
                                                    </span>
                                                </OverlayTrigger>
                                            </td>
                                            <PermissaoWrapper permissoesRequiridas={['administrador']}>
                                                <td style={{ backgroundColor: '#fffbf0', textAlign: 'center' }}>
                                                    <button
                                                        className="btn-enviar-termo"
                                                        onClick={() => handleAlterarResultado(candidato.candidatoId, candidato.nomeSocial || candidato.nome, candidato.resultado)}
                                                        title="Alterar resultado"
                                                    >
                                                        <BsPencilSquare size={14} />
                                                    </button>
                                                </td>
                                            </PermissaoWrapper>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            <div className="d-flex justify-content-between align-items-center mt-4">
                <div className="d-flex align-items-center gap-3">
                    <Form.Group className="mb-0">
                        <Form.Label className="me-2 mb-0">Itens por página:</Form.Label>
                        <Form.Select value={size} onChange={handleSizeChange} style={{width: 'auto', display: 'inline-block'}}>
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </Form.Select>
                    </Form.Group>
                    <small className="text-muted">Mostrando {candidatos.length} de {totalElements} candidatos</small>
                </div>

                {totalPages > 1 && (
                    <Pagination className="mb-0">
                        <Pagination.First onClick={() => handlePageChange(0)} disabled={page === 0} />
                        <Pagination.Prev onClick={() => handlePageChange(page - 1)} disabled={page === 0} />
                        {[...Array(totalPages).keys()].map((pageNumber) => (
                            <Pagination.Item key={pageNumber} active={pageNumber === page} onClick={() => handlePageChange(pageNumber)}>
                                {pageNumber + 1}
                            </Pagination.Item>
                        ))}
                        <Pagination.Next onClick={() => handlePageChange(page + 1)} disabled={page === totalPages - 1} />
                        <Pagination.Last onClick={() => handlePageChange(totalPages - 1)} disabled={page === totalPages - 1} />
                    </Pagination>
                )}
            </div>

            <ConfirmarEnvioTermoModal
                show={showModalEnvio}
                handleClose={() => setShowModalEnvio(false)}
                handleConfirm={dadosEnvio.quantidade ? confirmarEnvioTermoTodos : confirmarEnvioTermo}
                destinatario={dadosEnvio.nomeCompleto || ''}
                quantidade={dadosEnvio.quantidade}
            />

            <AlterarResultadoModal
                show={showModalResultado}
                handleClose={() => setShowModalResultado(false)}
                handleConfirm={confirmarAlteracaoResultado}
                nomeCandidato={candidatoSelecionado.nome}
                resultadoAtual={candidatoSelecionado.resultado}
            />
        </div>
    );
};

export default CandidatosBancaPage;